import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, ArrowRight, CheckCircle2 } from 'lucide-react';
import { BostonFederalName } from '../components/RegisteredName';

const serviceAreas = [
  'Financial Control & Infrastructure',
  'Strategic Financial Planning',
  'Ongoing Accounting Operations',
  'Reporting & Data Visibility',
  'Automation & Intelligent Operations',
  'Payroll & Workforce Compliance',
  'Bankability & Capital Readiness',
  'Portfolio Support',
  'General Inquiry',
];

const contactDetails = [
  {
    icon: MapPin,
    label: 'Office',
    value: 'Boston, Massachusetts',
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'info@bostonfederal.com',
    href: 'mailto:info@bostonfederal.com',
  },
  {
    icon: Phone,
    label: 'Consultations',
    value: 'By appointment',
  },
];

const nextSteps = [
  'We review your business stage, compliance environment, and financial control needs.',
  'A senior team member follows up within one business day.',
  'If there is a fit, we map the right operating model and next engagement path.',
];

export default function Contact() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const encode = data =>
    Object.keys(data)
      .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
      .join('&');

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'contact', ...form }),
      });
      setSubmitted(true);
    } catch {
      alert('There was a problem sending your message. Please email us directly at info@bostonfederal.com.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-16" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">
              Contact <BostonFederalName />
            </p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#0B1F33] leading-[1.05] tracking-tight mb-4">
              Start a Financial<br />
              <span className="text-[#1FA8F5]">Infrastructure Conversation</span>
            </h1>
            <p className="text-lg text-[#40516A] max-w-xl leading-relaxed">
              Whether you're looking to improve financial control, tackle complex compliance, or
              build the infrastructure for capital access, we will help identify the right path.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Contact Content ───────────────────────── */}
      <section className="bg-white py-16 pb-24" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Left: Info */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-bold text-[#0B1F33] mb-4">Get in Touch</h2>
              <p className="text-[#40516A] leading-relaxed mb-8 text-sm">
                <BostonFederalName /> works with businesses across industries to build the financial
                infrastructure, compliance systems, and operational control they need to grow.
                Reach out — we'll find the right path forward together.
              </p>

              <div className="space-y-6">
                {contactDetails.map(({ icon: Icon, label, value, href }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={16} strokeWidth={1.5} className="text-[#1FA8F5]" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B1F33] mb-1">{label}</div>
                      {href ? (
                        <a href={href} className="text-[#40516A] text-sm hover:text-[#1FA8F5] transition-colors">
                          {value}
                        </a>
                      ) : (
                        <div className="text-[#40516A] text-sm">{value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12">
                <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-5">What Happens Next</p>
                <div className="space-y-4">
                  {nextSteps.map((step, index) => (
                    <div key={step} className="flex items-start gap-4">
                      <span className="text-[#1FA8F5] font-bold text-sm mt-0.5">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <p className="text-[#40516A] text-sm leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Form */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-3"
            >
              {submitted ? (
                <div className="text-center py-16" data-testid="contact-success">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 size={32} strokeWidth={1.5} className="text-[#1FA8F5]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1F33] mb-2">Message Sent</h3>
                  <p className="text-[#40516A] mb-6">
                    Thank you for reaching out. We'll be in touch within one business day.
                  </p>
                  <Link
                    to="/"
                    className="text-[#1FA8F5] font-semibold text-sm hover:underline"
                  >
                    Return to Home
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-5"
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <input type="hidden" name="bot-field" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F33] mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        required
                        placeholder="John"
                        data-testid="contact-first-name"
                        className="w-full rounded-md px-4 py-3 text-[#0B1F33] text-sm placeholder-slate-300 bg-[#F7FAFC] focus:outline-none focus:ring-2 focus:ring-[#1FA8F5]/20 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F33] mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Smith"
                        data-testid="contact-last-name"
                        className="w-full rounded-md px-4 py-3 text-[#0B1F33] text-sm placeholder-slate-300 bg-[#F7FAFC] focus:outline-none focus:ring-2 focus:ring-[#1FA8F5]/20 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F33] mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Your Company Name"
                      data-testid="contact-company"
                      className="w-full rounded-md px-4 py-3 text-[#0B1F33] text-sm placeholder-slate-300 bg-[#F7FAFC] focus:outline-none focus:ring-2 focus:ring-[#1FA8F5]/20 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F33] mb-2">
                      Business Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      data-testid="contact-email"
                      className="w-full rounded-md px-4 py-3 text-[#0B1F33] text-sm placeholder-slate-300 bg-[#F7FAFC] focus:outline-none focus:ring-2 focus:ring-[#1FA8F5]/20 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F33] mb-2">
                      How Can We Help?
                    </label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      data-testid="contact-service-area"
                      className="w-full rounded-md px-4 py-3 text-sm text-[#0B1F33] focus:outline-none focus:ring-2 focus:ring-[#1FA8F5]/20 transition-colors bg-[#F7FAFC]"
                    >
                      <option value="">Select a service area...</option>
                      {serviceAreas.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B1F33] mb-2">
                      Tell Us About Your Business
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Share some background on your business and what you're looking to accomplish..."
                      data-testid="contact-message"
                      className="w-full rounded-md px-4 py-3 text-[#0B1F33] text-sm placeholder-slate-300 bg-[#F7FAFC] focus:outline-none focus:ring-2 focus:ring-[#1FA8F5]/20 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="bg-[#1FA8F5] text-white w-full py-3.5 rounded-md font-semibold hover:bg-[#0B8ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {loading ? 'Sending...' : (
                      <>Send Message <ArrowRight size={17} /></>
                    )}
                  </button>

                  <p className="text-xs text-[#6B7A90] leading-relaxed">
                    Your information is used only to review your inquiry and coordinate a response
                    from <BostonFederalName />. We do not use this form for mailing-list enrollment.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
